/**
 * Interactive Question Dispatcher
 *
 * Selects the appropriate renderer based on question type
 */

import { TrueFalse } from './TrueFalse';
import { MultipleSelect } from './MultipleSelect';
import { ShortAnswer } from './ShortAnswer';
import { Numeric } from './Numeric';
import { FillBlank } from './FillBlank';
import { Cloze } from './Cloze';
import { Sequence } from './Sequence';
import { Matching } from './Matching';
import { Categorization } from './Categorization';
import { ChessPuzzle } from '../ChessPuzzle';
import { Blackjack } from './Blackjack';
import type { BlackjackConfig } from '../PlayingCards';

// Type definitions matching backend
type QuestionType =
  | 'multiple_choice'
  | 'multiple_select'
  | 'true_false'
  | 'yes_no_maybe'
  | 'dropdown'
  | 'short_answer'
  | 'long_answer'
  | 'fill_blank'
  | 'cloze'
  | 'sequence'
  | 'matching'
  | 'categorization'
  | 'numeric'
  | 'range_slider'
  | 'likert'
  | 'rating'
  | 'hotspot'
  | 'label_diagram'
  | 'matrix_grid'
  | 'code_input'
  | 'chess_puzzle'
  | 'blackjack';

interface InteractiveQuestionProps {
  questionType: QuestionType;
  interactiveConfig: Record<string, unknown>;
  correctAnswer: string;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
  isCorrect?: boolean;
}

export function InteractiveQuestion({
  questionType,
  interactiveConfig,
  correctAnswer,
  onSubmit,
  disabled,
  showResult,
  isCorrect,
}: InteractiveQuestionProps) {
  switch (questionType) {
    case 'true_false':
      return (
        <TrueFalse
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          correctAnswer={correctAnswer}
        />
      );

    case 'multiple_select':
      return (
        <MultipleSelect
          config={interactiveConfig as { options: string[]; correctAnswers: string[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
        />
      );

    case 'short_answer':
      return (
        <ShortAnswer
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          isCorrect={isCorrect}
        />
      );

    case 'numeric':
      return (
        <Numeric
          config={interactiveConfig as { unit?: string; decimalPlaces?: number }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          isCorrect={isCorrect}
        />
      );

    case 'fill_blank':
      return (
        <FillBlank
          config={interactiveConfig as { textWithBlanks: string; acceptedAnswers: string[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          isCorrect={isCorrect}
        />
      );

    case 'cloze':
      return (
        <Cloze
          config={interactiveConfig as { textWithBlanks: string; blanks: { id: string; acceptedAnswers: string[] }[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          correctAnswer={correctAnswer}
        />
      );

    case 'sequence':
      return (
        <Sequence
          config={interactiveConfig as { items: string[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
        />
      );

    case 'matching':
      return (
        <Matching
          config={interactiveConfig as { pairs: { left: string; right: string }[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
        />
      );

    case 'categorization':
      return (
        <Categorization
          config={interactiveConfig as { categories: { name: string; items: string[] }[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
        />
      );

    case 'chess_puzzle':
      return (
        <ChessPuzzle
          config={interactiveConfig as { fen: string; moves?: string; rating?: number; themes?: string[] }}
          onSubmit={onSubmit}
          disabled={disabled}
          solution={correctAnswer}
          showSolution={showResult}
        />
      );

    case 'blackjack':
      return (
        <Blackjack
          config={interactiveConfig as unknown as BlackjackConfig}
          onSubmit={onSubmit}
          disabled={disabled}
          showResult={showResult}
          correctAnswer={correctAnswer}
        />
      );

    // Fallback for unsupported types
    default:
      return (
        <div style={{ padding: '1rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
          Question type "{questionType}" is not yet supported in the UI.
          <br />
          <ShortAnswer
            onSubmit={onSubmit}
            disabled={disabled}
            showResult={showResult}
            isCorrect={isCorrect}
            placeholder="Type your answer..."
          />
        </div>
      );
  }
}
